export type ProductMediaItem = {
  type: "image" | "video" | "youtube";
  url: string;
  caption: string;
};

export function youtubeId(value: string): string | null {
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    const host = url.hostname.toLowerCase();
    let id: string | null = null;
    if (host === "youtu.be") id = url.pathname.split("/")[1];
    if (
      [
        "youtube.com",
        "www.youtube.com",
        "m.youtube.com",
        "youtube-nocookie.com",
        "www.youtube-nocookie.com",
      ].includes(host)
    ) {
      id =
        url.pathname === "/watch"
          ? url.searchParams.get("v")
          : (/^\/(?:embed|shorts|live)\/([^/]+)/.exec(url.pathname)?.[1] ??
            null);
    }
    return id && /^[\w-]{11}$/.test(id) ? id : null;
  } catch {
    return null;
  }
}

export function validateProductMedia(value: unknown): ProductMediaItem[] {
  if (!Array.isArray(value) || value.length > 20)
    throw new Error("Add up to 20 gallery items.");
  return value.map((item, index) => {
    if (
      !item ||
      typeof item !== "object" ||
      !["image", "video", "youtube"].includes(item.type) ||
      typeof item.url !== "string"
    )
      throw new Error(`Gallery item ${index + 1} is invalid.`);
    const url = item.url.trim();
    if (!url || url.length > 2048)
      throw new Error(`Enter a URL for gallery item ${index + 1}.`);
    if (item.type === "youtube") {
      if (!youtubeId(url))
        throw new Error(
          `Gallery item ${index + 1} needs a valid YouTube video link.`,
        );
    } else if (
      url.startsWith("/") &&
      !url.startsWith("//") &&
      !/[\\\s]/.test(url)
    ) {
      // Existing uploads are served through the same-origin /api/media route.
    } else {
      try {
        const parsed = new URL(url);
        if (parsed.protocol !== "https:" || parsed.username || parsed.password)
          throw new Error();
      } catch {
        throw new Error(`Gallery item ${index + 1} needs an HTTPS media URL.`);
      }
    }
    if (item.caption !== undefined && typeof item.caption !== "string")
      throw new Error(`Gallery item ${index + 1} has an invalid caption.`);
    return {
      type: item.type,
      url,
      caption: (item.caption ?? "").trim().slice(0, 160),
    };
  });
}

export function readProductMedia(value: unknown): ProductMediaItem[] {
  try {
    return validateProductMedia(value ?? []);
  } catch {
    return [];
  }
}
